import { Helmet } from "react-helmet-async";

import {
  SITE_URL,
  SITE_NAME,
  SITE_SHORT_NAME,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
} from "../helpers/seo";

/*
  Per-page <head> tags. Render it once on every page; on detail pages render it
  after the data has loaded so the prerender captures the real title.

  <Seo
    title="ESG Consulting"            // becomes "ESG Consulting | ISA"; use fullTitle for an exact title
    description="120-160 characters"
    path="/Expertise/ESG"             // canonical path, exact casing
    image={coverUrl}                  // optional, absolute url or imported image
    type="article"                    // og:type, default "website"
    breadcrumbs={[{ name: "Expertise", path: "/Expertise" }, { name: "ESG", path: "/Expertise/ESG" }]}
    jsonLd={{ "@type": "Article", ... }} // object or array, "@context" is added
    article={{ publishedTime, modifiedTime, author, section }}
    lang="id"                         // when the page content is Indonesian
    noindex                           // forms, private and not-found pages
  />
*/
const Seo = ({
  title,
  fullTitle,
  description,
  path,
  image,
  imageAlt,
  type = "website",
  breadcrumbs,
  jsonLd,
  article,
  lang = "en",
  noindex = false,
}) => {
  const pageTitle =
    fullTitle || (title ? `${title} | ${SITE_SHORT_NAME}` : SITE_NAME);
  const canonical = path ? absoluteUrl(path) : undefined;
  const imageUrl = absoluteUrl(image || DEFAULT_OG_IMAGE);

  const schemas = [];
  if (breadcrumbs && breadcrumbs.length) {
    const items = [{ name: "Home", path: "/" }, ...breadcrumbs];
    schemas.push({
      "@type": "BreadcrumbList",
      itemListElement: items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: absoluteUrl(item.path),
      })),
    });
  }
  if (jsonLd) schemas.push(...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]));

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{pageTitle}</title>
      {description && <meta name="description" content={description} />}
      <meta
        name="robots"
        content={
          noindex ? "noindex, follow" : "index, follow, max-image-preview:large"
        }
      />
      {canonical && !noindex && <link rel="canonical" href={canonical} />}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle || title || SITE_NAME} />
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={imageAlt || title || SITE_NAME} />
      <meta property="og:locale" content={lang === "id" ? "id_ID" : "en_US"} />
      {article && article.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article && article.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article && article.author && (
        <meta property="article:author" content={article.author} />
      )}
      {article && article.section && (
        <meta property="article:section" content={article.section} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@isa_company" />
      <meta name="twitter:title" content={fullTitle || title || SITE_NAME} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imageUrl} />

      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", ...schema }).replace(
            /</g,
            "\\u003c"
          )}
        </script>
      ))}
    </Helmet>
  );
};

export const organizationRef = { "@id": `${SITE_URL}/#organization` };

export default Seo;
